import { Text, type TextProps } from '@chakra-ui/react';

export type SlugKind = 'player' | 'team';

/**
 * Format a stored slug for display.
 * - player: `@handle` (storage never includes `@`)
 * - team: uppercase 2–5 character tag (`ABC`) — not `@`, so teams stay distinct from players
 */
export function formatSlug(
  slug: string | null | undefined,
  kind: SlugKind = 'player'
): string {
  const clean = (slug ?? '').trim().replace(/^@+/, '');
  if (!clean) return '';
  return kind === 'team' ? clean.toUpperCase() : `@${clean}`;
}

export interface SlugTextProps extends Omit<TextProps, 'children'> {
  slug: string | null | undefined;
  kind?: SlugKind;
  /** Wrap team tags in brackets, e.g. `[ABC]`. Ignored for player handles. */
  brackets?: boolean;
}

/** Shared handle / team-tag display for profiles, cards, menus, etc. */
export function SlugText({
  slug,
  kind = 'player',
  brackets = false,
  fontFamily = 'mono',
  ...rest
}: SlugTextProps) {
  const formatted = formatSlug(slug, kind);
  if (!formatted) return null;
  const label = brackets && kind === 'team' ? `[${formatted}]` : formatted;
  return (
    <Text as="span" fontFamily={fontFamily} {...rest}>
      {label}
    </Text>
  );
}
